import React from 'react';

const getBarColorClass = (percentage) => {
  if (percentage >= 80) return 'bg-primary-500';
  if (percentage >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const parseQualityReportToHTML = (reportArray) => {
  if (!Array.isArray(reportArray) || reportArray.length === 0) {
    return <p>Ensure that your article is well formatted and SEO friendly.</p>;
  }

  const report = reportArray[0]?.quality_control_report;
  if (!report || typeof report !== 'object') {
    return <p>Quality report format is invalid.</p>;
  }

  const renderProgressBar = (score, maxScore) => {
    const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
    return (
      <div className="flex-1 mx-0">
        <div className="relative w-full bg-neutral-200 rounded-full overflow-hidden" style={{ height: '8px' }}>
          <div
            className={`absolute inset-0 rounded-full transition-all duration-500 ${getBarColorClass(percentage)}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  const getContextText = (key, value) => {
    if (key === 'Word Count' && value?.count !== undefined) {
      return `Ideal article length is ${value.ideal || '800-1200 words'}, your count is`;
    }
    if (key === 'Title Length' && value?.length !== undefined) {
      return 'Ideal title length is 50-60 characters, your length is';
    }
    if (key === 'Headings Count' && value?.counts) {
      return 'Score based on: H1 (must be 1) = 10pts, H2 (at least 1) = 10pts, H3 (at least 1) = 5pts';
    }
    return null;
  };

  const getMetricValue = (key, value) => {
    if (key === 'Word Count' && value?.count !== undefined) return value.count;
    if (key === 'Title Length' && value?.length !== undefined) return value.length;
    return null;
  };

  const renderCountsGrid = (counts) => (
    <div className="mt-3 grid grid-cols-1 gap-2">
      {Object.entries(counts).map(([label, count]) => (
        <div
          className="flex items-center justify-between bg-neutral-50 rounded-lg px-3 py-2 text-sm text-gray-700"
          key={label}
        >
          <span className="pr-2 break-words">{label}</span>
          <span className="font-semibold text-gray-900 shrink-0">{count}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {Object.entries(report).map(([key, value]) => {
        if (key === 'Overall Score') return null;

        const hasScore = value?.score !== undefined && value?.maxScore !== undefined;
        const contextText = getContextText(key, value);
        const metricValue = getMetricValue(key, value);
        const scorePercentage = hasScore && value.maxScore > 0 ? Math.round((value.score / value.maxScore) * 100) : 0;

        if (key === 'Keyword Check' && value?.counts) {
          return (
            <div key={key} className="pb-6 mb-6 border-b border-gray-200 last:border-0">
              <div className="space-y-2">
                <span className="text-base font-semibold text-gray-900">Keyword Check</span>
                {value.description && (
                  <p className="text-xs text-gray-600">{value.description}</p>
                )}
                {renderCountsGrid(value.counts)}
              </div>
            </div>
          );
        }

        if (!hasScore) return null;

        return (
          <div key={key} className="pb-6 mb-6 border-b border-gray-200 last:border-0">
            <div className="space-y-2">
              <span className="text-base font-semibold text-gray-900">{key} Score: {scorePercentage}%</span>
              {renderProgressBar(value.score, value.maxScore)}
              {contextText && (
                <div className="text-xs text-gray-700 mt-2">
                  {metricValue !== null ? (
                    <>
                      {contextText} <span className="font-medium text-gray-900">{metricValue}</span>
                    </>
                  ) : (
                    <>{contextText}</>
                  )}
                </div>
              )}
              {key === 'Keyword Density' && value?.density && (
                <div className="text-xs text-gray-700 mt-1">
                  Primary keyword density: <span className="font-medium text-gray-900">{value.density}</span>
                  {value.isIdeal && (
                    <> · Ideal range (1–2%): <span className="font-medium text-gray-900">{value.isIdeal}</span></>
                  )}
                </div>
              )}
              {key === 'Headings Count' && value?.counts && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {Object.entries(value.counts).map(([heading, count]) => (
                    <span className="inline-block bg-neutral-50 rounded-lg px-3 py-1.5 text-sm text-gray-700" key={heading}>
                      {`${heading}: ${count}`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default parseQualityReportToHTML;
